import React, {useEffect, useRef, useState} from "react";
import {useAuthInfo} from "@propelauth/react";
import {Download} from "lucide-react";
import {postWithToken} from "../../api/apiBase";
import {Button} from "../ui/button";
import {Spinner} from "../ui/spinner";


type ViewerFile = {
    id: string;
    name: string;
}

type FileViewerProps = {
    project_id: string;
    file: ViewerFile;
}

const SHEETS = ["xlsx", "xlsm", "xls", "csv"];
// a bad file can say it is a million rows long - show the top left of it
const MAX_ROWS = 2000;
const MAX_COLS = 100;

export const extOf = (name: string) => name.includes(".") ? (name.split(".").pop() as string).toLowerCase() : "";

const errText = (err: any) => String(err?.message ?? err);

/**
 * a presigned s3 url - server: files/download/v1. it checks the file's own context, so the id is enough.
 * inline - a pdf the browser shows instead of downloading. other links expire in seconds - use them right away
 * */
export function fileLink(project_id: string, id: string, token: string, inline = false): Promise<string> {
    return postWithToken("/files/download/v1", JSON.stringify({project_id, id, inline}), token)
        .then((data) => data.url);
}

export function downloadFile(project_id: string, id: string, token: string) {
    return fileLink(project_id, id, token).then((url) => window.open(url, "_blank"));
}

// the file itself - needs the bucket's cors to allow GET (it does: aws_service.create_new_bucket)
async function fetchBytes(url: string): Promise<ArrayBuffer> {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`download failed (${res.status})`);
    return res.arrayBuffer();
}


function PdfView({url, name}: { url: string, name: string }) {
    // the browser's own pdf viewer
    return <iframe title={name} src={url} className="w-full h-full rounded border"/>;
}

/**
 * word - docx-preview lays out the pages in the browser. loaded on first use, it is not in the main bundle
 * */
function DocxView({url}: { url: string }) {
    const ref = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        Promise.all([import("docx-preview"), fetchBytes(url)])
            .then(([docx, data]) => docx.renderAsync(data, ref.current as HTMLElement))
            .then(() => {
                // links are copied from the document as they are - keep web links (in a new tab), drop the rest
                ref.current?.querySelectorAll("a[href]").forEach((a) => {
                    if (/^https?:/i.test(a.getAttribute("href") ?? "")) {
                        a.setAttribute("target", "_blank");
                        a.setAttribute("rel", "noopener noreferrer");
                    } else if (!(a.getAttribute("href") ?? "").startsWith("#")) {
                        a.removeAttribute("href");
                    }
                });
            })
            .catch((err) => setError(errText(err)))
            .finally(() => setLoading(false));
    }, [url]);

    return <>
        {loading && <Spinner/>}
        {error && <div className="text-sm text-destructive">Could not open the document: {error}</div>}
        <div ref={ref}/>
    </>
}


type Sheet = {
    name: string;
    cols: string[];         // column letters - A, B, C...
    firstRow: number;       // excel row number of rows[0]
    rows: string[][];       // as excel shows them (formatted)
    clipped: boolean;
}

/**
 * excel (and csv) - SheetJS reads it, one tab per sheet, a plain table of what each cell shows.
 * merged cells show in their top left cell. loaded on first use, it is not in the main bundle
 * */
function SheetView({url}: { url: string }) {
    const [sheets, setSheets] = useState<Sheet[] | null>(null);
    const [current, setCurrent] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        Promise.all([import("xlsx"), fetchBytes(url)])
            .then(([XLSX, data]) => {
                const book = XLSX.read(data, {type: "array"});
                setSheets(book.SheetNames.map((name) => {
                    const ws = book.Sheets[name];
                    const full = XLSX.utils.decode_range(ws["!ref"] ?? "A1");
                    const range = {
                        s: full.s,
                        e: {r: Math.min(full.e.r, full.s.r + MAX_ROWS - 1), c: Math.min(full.e.c, full.s.c + MAX_COLS - 1)},
                    };
                    return {
                        name,
                        cols: Array.from({length: range.e.c - range.s.c + 1}, (_, i) => XLSX.utils.encode_col(range.s.c + i)),
                        firstRow: range.s.r + 1,
                        rows: ws["!ref"]
                            ? XLSX.utils.sheet_to_json<string[]>(ws, {header: 1, raw: false, defval: "", blankrows: true, range})
                            : [],
                        clipped: range.e.r < full.e.r || range.e.c < full.e.c,
                    };
                }));
            })
            .catch((err) => setError(errText(err)));
    }, [url]);

    if (error) return <div className="text-sm text-destructive">Could not open the spreadsheet: {error}</div>;
    if (sheets === null) return <Spinner/>;
    const sheet = sheets[current];
    if (!sheet) return <div className="text-sm text-muted-foreground">This spreadsheet is empty.</div>;

    return <div className="flex flex-col h-full min-h-0">
        {sheets.length > 1 &&
            <div className="flex gap-1 border-b mb-1 overflow-x-auto shrink-0">
                {sheets.map((s, i) =>
                    <button key={i} type="button" onClick={() => setCurrent(i)}
                            className={"px-2 py-1 text-xs whitespace-nowrap " +
                                (i === current ? "border-b-2 border-primary font-medium" : "text-muted-foreground")}>
                        {s.name}
                    </button>)}
            </div>}
        {sheet.clipped &&
            <div className="text-xs text-muted-foreground mb-1">
                Showing the first {MAX_ROWS} rows and {MAX_COLS} columns - download it to see the rest.
            </div>}
        <div className="flex-1 min-h-0 overflow-auto">
            <table className="text-xs border-collapse">
                <thead>
                <tr>
                    <th className="sticky top-0 bg-muted border px-1"/>
                    {sheet.cols.map((c) =>
                        <th key={c} className="sticky top-0 bg-muted border px-1 font-normal text-muted-foreground">{c}</th>)}
                </tr>
                </thead>
                <tbody>
                {sheet.rows.map((row, r) =>
                    <tr key={r}>
                        <td className="bg-muted border px-1 text-right text-muted-foreground">{sheet.firstRow + r}</td>
                        {sheet.cols.map((c, i) =>
                            <td key={c} className="border px-1 whitespace-nowrap">{row[i] ?? ""}</td>)}
                    </tr>)}
                </tbody>
            </table>
        </div>
    </div>
}


/**
 * Shows a file in the page - pdf, word (docx) and excel (xlsx, xls, csv). Anything else gets a download button.
 * Fills its parent - give it a height.
 * */
export function FileViewer({project_id, file}: FileViewerProps) {
    const {accessToken} = useAuthInfo();
    const ext = extOf(file.name);
    const viewable = ext === "pdf" || ext === "docx" || SHEETS.includes(ext);
    // the link and the file it is for - a new file shows a spinner until its own link is back
    const [link, setLink] = useState<{ id: string, url: string } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const url = link?.id === file.id ? link.url : null;

    useEffect(() => {
        let live = true;
        setError(null);
        if (!viewable) return;
        fileLink(project_id, file.id, accessToken as string, ext === "pdf")
            .then((url) => live && setLink({id: file.id, url}))
            .catch((err) => live && setError(errText(err)));
        // switched files before the link came back - drop it
        return () => {
            live = false;
        };
    }, [file.id]);

    if (!viewable) {
        return <div className="flex flex-col items-start gap-2 text-sm text-muted-foreground p-2">
            No preview for {ext ? `.${ext}` : "this"} files.
            <Button variant="outline" size="sm" type="button"
                    onClick={() => downloadFile(project_id, file.id, accessToken as string)}>
                <Download className="w-3 h-3 mr-1"/> Download
            </Button>
        </div>
    }
    if (error) return <div className="text-sm text-destructive p-2">Could not open the file: {error}</div>;
    if (!url) return <Spinner/>;

    // keyed by url - a new file starts a new viewer, an old one still loading can't draw over it
    return <div className="h-full min-h-0 overflow-auto">
        {ext === "pdf" && <PdfView key={url} url={url} name={file.name}/>}
        {ext === "docx" && <DocxView key={url} url={url}/>}
        {SHEETS.includes(ext) && <SheetView key={url} url={url}/>}
    </div>
}
